import React from 'react';
import PropTypes from 'prop-types';
import T from 'i18n-react';
import camelCase from 'lodash/camelCase';
import curry from 'lodash/curry';

import { Spacer } from '@motorway/motorway-component-library';

import { triggerCheckboxGroupFilterSetEvent } from 'Services/analytics/events';
import useVehicleFilterStore from 'Stores/FilterStore/VehicleListFilter/VehicleListFilterStore';
import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';

import {
	SELLER_TYPES,
	SERVICE_HISTORY_TYPES,
	VEHICLE_LIST_FILTER_NAME,
	VEHICLE_LIST_FILTERS_ID,
} from '../VehicleListFilters.consts';

import CheckboxGroupFilterView from './CheckboxGroup/CheckboxGroupFilterView';
import { FuelOptions, NumericGradeOptions, SellerTypesOptions, TransmissionOptions } from './Filters.helpers';

import styles from './NewFilters.module.scss';

T.setTexts(require('Components/texts.json'));

const { FUEL, NUMERIC_GRADE, SELLER_TYPE, SERVICE_HISTORY, TRANSMISSION } = VEHICLE_LIST_FILTER_NAME;

const checkboxOptions = (options, key) =>
	options.map((value) => ({
		label: T.translate(`vehicleList.filters.${key}.${camelCase(value)}`),
		value,
	}));

const ButtonsSection = ({ showSellerTypeFilter }) => {
	const filters = useVehicleFilterStore((store) => store.filters);
	const setValue = useVehicleFilterStore((store) => store.setValue);
	const isExLeaseBrandingEnabled = useFeatureToggle(FEATURES.exLeaseBranding);
	const isPartnerVisibilityEnabled = useFeatureToggle(FEATURES.partnerVehicleVisibility);

	const sellerTypesOptions = isPartnerVisibilityEnabled
		? SellerTypesOptions.filter((type) => type !== SELLER_TYPES.TRADE)
		: SellerTypesOptions.filter((type) => type !== SELLER_TYPES.FLEET);
	const sellerTypeCheckboxOptions = checkboxOptions(sellerTypesOptions, VEHICLE_LIST_FILTERS_ID[SELLER_TYPE]).map(
		(option) => {
			if (option.value === SELLER_TYPES.FLEET) {
				return { ...option, label: T.translate('vehicleList.filters.sellerTypes.exLease') };
			}
			if (isExLeaseBrandingEnabled && option.value === SELLER_TYPES.TRADE) {
				return { ...option, label: T.translate('vehicleList.filters.sellerTypes.exLease') };
			}
			return option;
		},
	);
	const { fuel, numericGrade, sellerType, serviceHistory, transmission } = filters;

	const filterViews = [
		{
			id: VEHICLE_LIST_FILTERS_ID[SERVICE_HISTORY],
			key: SERVICE_HISTORY,
			options: SERVICE_HISTORY_TYPES,
			values: serviceHistory,
		},
		{
			id: VEHICLE_LIST_FILTERS_ID[NUMERIC_GRADE],
			key: NUMERIC_GRADE,
			options: NumericGradeOptions,
			values: numericGrade,
		},
		{
			id: VEHICLE_LIST_FILTERS_ID[FUEL],
			key: FUEL,
			options: FuelOptions,
			values: fuel,
		},
		{
			id: VEHICLE_LIST_FILTERS_ID[TRANSMISSION],
			key: TRANSMISSION,
			options: TransmissionOptions,
			values: transmission,
		},
		...(showSellerTypeFilter
			? [
					{
						columns: {
							desktop: 3,
							mobile: 3,
						},
						customOptions: sellerTypeCheckboxOptions,
						id: VEHICLE_LIST_FILTERS_ID[SELLER_TYPE],
						key: SELLER_TYPE,
						options: sellerTypesOptions,
						values: sellerType,
					},
				]
			: []),
	];

	const handleOnClick = curry((section, clickedOption, value, currentlySelected) => {
		setValue({ id: section, value: [...currentlySelected] });

		if (!clickedOption) {
			return;
		}

		triggerCheckboxGroupFilterSetEvent({ clickedOption, section, value });
	});

	return (
		<>
			{filterViews.map(({ badge, columns, customOptions, id, key, options, values }, index) => (
				<React.Fragment key={key}>
					<CheckboxGroupFilterView
						badge={() => badge ?? null}
						checked={[...values]}
						columns={columns}
						id={id}
						name={key}
						onClick={handleOnClick(key)}
						options={customOptions || checkboxOptions(options, id)}
						title={T.translate(`vehicleList.filters.${key}`)}
					/>
					{index + 1 < filterViews.length && <Spacer showDivider className={styles.divider} size="zero" />}
				</React.Fragment>
			))}
		</>
	);
};

ButtonsSection.propTypes = {
	showSellerTypeFilter: PropTypes.bool.isRequired,
};

export default ButtonsSection;
